import { useRef, useState } from "react"

import {
  hashKey,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import {
  type FlowPluginBindings,
  type PluginConsentPreview,
  type PluginEntry,
  pluginsApi,
} from "@/api/plugins-api"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useDebouncedValue } from "@/hooks/use-debounced-value"

import { BackgroundAccountPanel } from "./background-account-panel"
import { pluginErrorCode, pluginErrorMessage } from "./plugin-utils"
import { usePluginQueryKey, usePluginSessionGeneration } from "./use-plugin-query-key"

/** Schedule forms edit settings on every keystroke; only preview once typing settles. */
const CONSENT_PREVIEW_DEBOUNCE_MS = 400

/** Order-insensitive, matching how react-query hashes the same object into a key. */
const serializeSettings = (settings: Record<string, unknown>) => hashKey([settings])

export function FlowPluginPicker({
  value,
  onChange,
}: {
  value: FlowPluginBindings
  onChange: (value: FlowPluginBindings) => void
}) {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const catalog = useQuery({ queryKey: pluginKey("catalog"), queryFn: pluginsApi.catalog })
  if (!catalog.data?.enabled) return null
  const entries = catalog.data.items.filter(
    (entry) => entry.installed && entry.surfaces.includes("flows")
  )
  return (
    <fieldset className="nodrag nowheel space-y-2 rounded-lg border p-3">
      <legend className="px-1 text-sm font-medium">{t("plugins.title")}</legend>
      {!entries.length && (
        <p className="text-muted-foreground text-xs">{t("plugins.flowInstallFirst")}</p>
      )}
      {entries.map((entry) => (
        <div key={entry.id} className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={Boolean(value[entry.id])}
              onCheckedChange={(checked) => {
                const next = { ...value }
                if (checked)
                  next[entry.id] = {
                    operation_ids: [],
                    connection_id: entry.connection?.id ?? null,
                  }
                else delete next[entry.id]
                onChange(next)
              }}
            />
            {entry.name}
          </label>
          {value[entry.id] && (
            <FlowPluginOperations
              entry={entry}
              selected={value[entry.id]?.operation_ids ?? []}
              onChange={(operations) =>
                onChange({
                  ...value,
                  [entry.id]: {
                    ...value[entry.id],
                    operation_ids: operations,
                    connection_id: entry.connection?.id ?? null,
                  },
                })
              }
            />
          )}
        </div>
      ))}
    </fieldset>
  )
}

function FlowPluginOperations({
  entry,
  selected,
  onChange,
}: {
  entry: PluginEntry
  selected: string[]
  onChange: (selected: string[]) => void
}) {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const operations = useQuery({
    queryKey: pluginKey("operations", entry.id),
    queryFn: () => pluginsApi.operations(entry.id),
  })
  return (
    <div className="ml-4 space-y-2">
      <p className="text-muted-foreground text-xs">
        {entry.credential_mode === "company"
          ? t("plugins.companyAccount")
          : t("plugins.personalAccount")}
      </p>
      {operations.error && (
        <p className="text-destructive text-xs" role="alert">
          {t("plugins.error")}
        </p>
      )}
      <div className="max-h-40 space-y-1 overflow-auto">
        {operations.data?.operations.map((op) => (
          <label key={op.id} className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={selected.includes(op.id)}
              onCheckedChange={(checked) =>
                onChange(checked ? [...selected, op.id] : selected.filter((id) => id !== op.id))
              }
            />
            {op.id}
          </label>
        ))}
      </div>
    </div>
  )
}

export function FlowPluginToolConfig({
  config,
  onChange,
}: {
  config: Record<string, unknown>
  onChange: (config: Record<string, unknown>, bindings: FlowPluginBindings) => void
}) {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const [raw, setRaw] = useState(JSON.stringify(config.arguments ?? {}, null, 2))
  const [invalid, setInvalid] = useState(false)
  const catalog = useQuery({ queryKey: pluginKey("catalog"), queryFn: pluginsApi.catalog })
  const pluginId = typeof config.plugin_id === "string" ? config.plugin_id : ""
  const operationId = typeof config.operation_id === "string" ? config.operation_id : ""
  const entries =
    catalog.data?.items.filter((entry) => entry.installed && entry.surfaces.includes("flows")) ?? []
  const operations = useQuery({
    queryKey: pluginKey("operations", pluginId),
    queryFn: () => pluginsApi.operations(pluginId),
    enabled: Boolean(pluginId),
  })
  function update(next: Record<string, unknown>) {
    const id = String(next.plugin_id ?? "")
    const entry = entries.find((item) => item.id === id)
    onChange(
      next,
      id
        ? {
            [id]: {
              operation_ids: next.operation_id ? [String(next.operation_id)] : [],
              connection_id: entry?.connection?.id ?? null,
            },
          }
        : {}
    )
  }
  return (
    <div className="nodrag nowheel space-y-2">
      <Label>{t("plugins.title")}</Label>
      <Select
        value={pluginId}
        onValueChange={(id) => update({ plugin_id: id, operation_id: "", arguments: {} })}
      >
        <SelectTrigger>
          <SelectValue placeholder={t("plugins.title")} />
        </SelectTrigger>
        <SelectContent>
          {entries.map((entry) => (
            <SelectItem key={entry.id} value={entry.id}>
              {entry.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={operationId} onValueChange={(id) => update({ ...config, operation_id: id })}>
        <SelectTrigger>
          <SelectValue placeholder={t("plugins.operation")} />
        </SelectTrigger>
        <SelectContent>
          {operations.data?.operations.map((op) => (
            <SelectItem key={op.id} value={op.id}>
              {op.id}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Label htmlFor="flow-plugin-arguments">{t("plugins.arguments")}</Label>
      <Textarea
        id="flow-plugin-arguments"
        value={raw}
        onChange={(event) => {
          setRaw(event.target.value)
          try {
            const parsed: unknown = JSON.parse(event.target.value)
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
              throw new Error("Invalid object")
            update({ ...config, arguments: parsed })
            setInvalid(false)
          } catch {
            setInvalid(true)
          }
        }}
      />
      {invalid && (
        <p className="text-destructive text-xs" role="alert">
          {t("plugins.invalidArguments")}
        </p>
      )}
    </div>
  )
}

export function FlowPluginConsent({
  flowId,
  settings,
  showAccount = true,
}: {
  flowId: string
  settings: Record<string, unknown>
  showAccount?: boolean
}) {
  const generation = usePluginSessionGeneration()
  return (
    <FlowPluginConsentContent
      key={generation}
      flowId={flowId}
      settings={settings}
      showAccount={showAccount}
    />
  )
}

function FlowPluginConsentContent({
  flowId,
  settings,
  showAccount,
}: {
  flowId: string
  settings: Record<string, unknown>
  showAccount: boolean
}) {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const client = useQueryClient()
  // The user agrees to the settings they typed, not to a preview hash: the tick
  // is bound to the settings content so a preview landing later (debounce, refetch)
  // cannot silently untick it. Editing the settings does clear it.
  const settingsKey = serializeSettings(settings)
  const [agreedFor, setAgreedFor] = useState<string | null>(null)
  const agreed = agreedFor === settingsKey
  const catalog = useQuery({ queryKey: pluginKey("catalog"), queryFn: pluginsApi.catalog })
  const [debouncedSettings, settingsPending] = useDebouncedValue(
    settings,
    CONSENT_PREVIEW_DEBOUNCE_MS,
    serializeSettings
  )
  const preview = useQuery({
    queryKey: pluginKey("consent", flowId, debouncedSettings),
    queryFn: () => pluginsApi.consentPreview(flowId, debouncedSettings),
    enabled: catalog.data?.enabled === true,
    placeholderData: keepPreviousData,
  })
  // Keep the last preview we actually received on screen: a refetch, a keystroke
  // or a failed preview must never make the account/consent section disappear.
  // Key it by the settings that produced it so a failed B preview cannot unlock
  // Allow (or grant) with leftover hashes from A.
  const lastPreview = useRef<{ settingsKey: string; data: PluginConsentPreview } | null>(null)
  if (preview.data && !preview.isPlaceholderData) {
    lastPreview.current = { settingsKey: serializeSettings(debouncedSettings), data: preview.data }
  }
  const shown = preview.data ?? lastPreview.current?.data ?? null
  const approvalKey = shown?.approval_hash ?? ""
  // Grant posts the *current* settings together with the reviewed hashes, so it
  // may only run while the preview on screen describes exactly those settings.
  // keepPreviousData applies only while pending; on error, data is undefined and
  // isPlaceholderData/isFetching are false, so lastPreview (still A) must not
  // count as a match for typed B.
  const previewInFlight = settingsPending || preview.isPlaceholderData || preview.isFetching
  const previewStale =
    previewInFlight || preview.isError || lastPreview.current?.settingsKey !== settingsKey
  const grant = useMutation({
    mutationFn: () => {
      const reviewed = shown
      if (!reviewed || lastPreview.current?.settingsKey !== settingsKey)
        return Promise.reject(new Error("stale-preview"))
      return pluginsApi.grantConsent(flowId, settings, reviewed)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: pluginKey("consent", flowId) }),
  })
  if (!catalog.data?.enabled || (!preview.error && !shown?.connections.length && !shown?.identity))
    return null
  return (
    <div aria-busy={previewInFlight} className="space-y-2 rounded-lg border p-3">
      {showAccount ? <BackgroundAccountPanel flowId={flowId} /> : null}
      <p className="font-medium">{t("plugins.backgroundTitle")}</p>
      <p className="text-muted-foreground text-sm">{t("plugins.backgroundDescription")}</p>
      <ul className="text-sm">
        {shown?.assignments?.map((assignment) => (
          <li key={assignment.name}>
            {assignment.name} ·{" "}
            {t(
              {
                company: "plugins.companyAccount",
                personal: "plugins.personalAccount",
                none: "plugins.auth.none",
              }[assignment.account_mode]
            )}
            <span className="text-muted-foreground"> · {assignment.operations.join(", ")}</span>
          </li>
        ))}
      </ul>
      {shown?.requires_consent && (
        <>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={agreed}
              onCheckedChange={(value) => setAgreedFor(value === true ? settingsKey : null)}
            />
            {t("plugins.backgroundAgree")}
          </label>
          <Button
            variant="outline"
            disabled={
              !approvalKey ||
              previewStale ||
              !agreed ||
              grant.isPending ||
              (Boolean(shown.identity) && shown.identity?.status !== "connected")
            }
            onClick={() => grant.mutate()}
          >
            {t("plugins.backgroundAllow")}
          </Button>
        </>
      )}
      {shown && !shown.requires_consent && (
        <p className="text-sm">{t("plugins.backgroundGranted")}</p>
      )}
      {previewInFlight && (
        <p className="text-muted-foreground text-xs" role="status">
          {t("common.loading")}
        </p>
      )}
      {(preview.error || grant.error) && (
        <p className="text-destructive text-sm" role="alert">
          {pluginErrorMessage(t, pluginErrorCode(preview.error ?? grant.error))}
        </p>
      )}
    </div>
  )
}
